import { useEffect, useState } from 'react';
import { Loader2, Clock } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { atendimentoApi } from '../lib/api';
import type { HorarioDia } from '../lib/tipos';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

const DIAS = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];

function trimSeg(t: string): string {
  return t?.length === 8 ? t.slice(0, 5) : t;
}

export function ModalHorarios({ aberto, onFechar }: Props) {
  const toast = useToast();
  const [carregando, setCarregando] = useState(false);
  const [salvandoDia, setSalvandoDia] = useState<number | null>(null);
  const [dias, setDias] = useState<HorarioDia[]>([]);

  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setCarregando(true);
    atendimentoApi.listarHorario()
      .then((lista) => { if (!cancelado) setDias(lista); })
      .catch(() => toast.erro('Falha ao carregar horarios'))
      .finally(() => !cancelado && setCarregando(false));
    return () => { cancelado = true; };
  }, [aberto, toast]);

  function patchLocal(dia: number, patch: Partial<HorarioDia>) {
    setDias((prev) => prev.map((d) => (d.dia_semana === dia ? { ...d, ...patch } : d)));
  }

  async function salvar(dia: HorarioDia, patch: Partial<Pick<HorarioDia, 'abre_em' | 'fecha_em' | 'ativo' | 'mensagem_auto_reply'>>) {
    setSalvandoDia(dia.dia_semana);
    try {
      const atualizado = await atendimentoApi.atualizarDiaHorario(dia.dia_semana, patch);
      patchLocal(dia.dia_semana, atualizado);
      toast.sucesso(`${DIAS[dia.dia_semana]} salvo`);
    } catch (e: any) {
      toast.erro(e?.message || 'Falha ao salvar');
    } finally {
      setSalvandoDia(null);
    }
  }

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Horario de atendimento" largura={680}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      <div className="mb-3 text-xs opacity-70 flex items-start gap-2">
        <Clock size={14} className="mt-0.5 flex-shrink-0" />
        <span>Quando uma mensagem chega fora do horario do dia (ou no dia <b>desativado</b>), o sistema responde 1x/dia/contato com a <b>mensagem auto</b> daquele dia.</span>
      </div>

      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {!carregando && dias.length > 0 && (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {dias.map((d) => (
            <div key={d.dia_semana} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer min-w-[140px]">
                  <input
                    type="checkbox"
                    checked={d.ativo}
                    onChange={(e) => salvar(d, { ativo: e.target.checked })}
                    disabled={salvandoDia === d.dia_semana}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-sm">{DIAS[d.dia_semana]}</span>
                </label>

                <div className="flex items-center gap-1.5">
                  <input
                    type="time"
                    value={trimSeg(d.abre_em)}
                    onChange={(e) => patchLocal(d.dia_semana, { abre_em: e.target.value + ':00' })}
                    onBlur={(e) => salvar(d, { abre_em: e.target.value + ':00' })}
                    disabled={!d.ativo || salvandoDia === d.dia_semana}
                    className="px-2 py-1 rounded border text-sm bg-white/5 disabled:opacity-50"
                    style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
                  />
                  <span className="text-xs opacity-60">ate</span>
                  <input
                    type="time"
                    value={trimSeg(d.fecha_em)}
                    onChange={(e) => patchLocal(d.dia_semana, { fecha_em: e.target.value + ':00' })}
                    onBlur={(e) => salvar(d, { fecha_em: e.target.value + ':00' })}
                    disabled={!d.ativo || salvandoDia === d.dia_semana}
                    className="px-2 py-1 rounded border text-sm bg-white/5 disabled:opacity-50"
                    style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
                  />
                </div>

                {salvandoDia === d.dia_semana && <Loader2 size={14} className="animate-spin opacity-60" />}
              </div>

              <textarea
                value={d.mensagem_auto_reply}
                onChange={(e) => patchLocal(d.dia_semana, { mensagem_auto_reply: e.target.value })}
                onBlur={(e) => {
                  if (e.target.value !== dias.find((x) => x.dia_semana === d.dia_semana)?.mensagem_auto_reply) {
                    salvar(d, { mensagem_auto_reply: e.target.value });
                  }
                }}
                rows={2}
                placeholder="Mensagem automatica fora do horario..."
                disabled={salvandoDia === d.dia_semana}
                className="w-full mt-2 px-2 py-1.5 rounded border text-sm bg-white/5 resize-y disabled:opacity-50"
                style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))' }}
                maxLength={2000}
              />
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] opacity-60 mt-3 leading-relaxed">
        <b>Auto-save:</b> alteracoes sao salvas ao sair do campo (blur). Toggle do dia salva imediatamente.
      </div>
    </Modal>
  );
}
